"use client";
// Bavimail's one modal. Built from beui.dev/components/motion/center-morph-modal
// (`pnpm dlx shadcn@latest add @beui/center-morph-modal` writes center-morph-modal.tsx
// next to this file; port upstream fixes across by hand).
// Restyled for Bavimail (DESIGN.md 4 Elevation, 5 Cards): sharp dashed panel on
// `surface`, corner-dot register marks, page-tinted backdrop, modal shadow.
// Entrance: beUI's centre unfold (clip-path) is replaced by the quieter fade +
// slight scale that beUI's own `morphing-modal` uses, on the shared panel spring.
// Bavimail additions: `size`, `role`, `busy`, `onExitComplete`, focus return, and the
// Header / Body / Footer parts so every dialog shares one anatomy.

import { X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  cloneElement,
  createContext,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { EASE_OUT, SPRING_PANEL } from "@/lib/ease";
import { useOverlayLifecycle } from "@/lib/hooks/use-overlay-lifecycle";
import { PresenceGate } from "@/lib/presence-gate";
import { cn } from "@/lib/utils";

type ModalContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerId: string;
  contentId: string;
};

const ModalContext = createContext<ModalContextValue | null>(null);

function useModalContext(component: string) {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error(`${component} must be used within <Modal>`);
  }
  return context;
}

export interface ModalProps {
  children: ReactNode;
  /** Controlled open state. */
  open?: boolean;
  /** Initial state when used uncontrolled. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * A request is in flight: ignore every dismissal (Escape, backdrop, the close
   * control, `ModalClose`) until it settles.
   */
  busy?: boolean;
}

/**
 * A centred modal that fades and scales in.
 * Supports controlled and uncontrolled state through composable primitives.
 */
export function Modal({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  busy = false,
}: ModalProps) {
  const id = useId();
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (busy && !next) return;
      if (!controlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [busy, controlled, onOpenChange],
  );

  const value = useMemo<ModalContextValue>(
    () => ({
      open,
      setOpen,
      triggerId: `${id}-trigger`,
      contentId: `${id}-content`,
    }),
    [id, open, setOpen],
  );

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
}

export interface ModalTriggerProps {
  children: ReactElement;
}

/** Wraps one interactive element and opens or closes the modal. */
export function ModalTrigger({ children }: ModalTriggerProps) {
  const context = useModalContext("ModalTrigger");
  if (!isValidElement(children)) return children;

  const child = children as ReactElement<Record<string, unknown>>;
  const childOnClick = child.props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  return cloneElement(child, {
    id: context.triggerId,
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      childOnClick?.(event);
      if (!event.defaultPrevented) context.setOpen(!context.open);
    },
    "aria-haspopup": "dialog",
    "aria-expanded": context.open,
    "aria-controls": context.open ? context.contentId : undefined,
  });
}

export interface ModalCloseProps {
  children: ReactElement;
}

/** Wraps one interactive element and closes the modal. */
export function ModalClose({ children }: ModalCloseProps) {
  const context = useModalContext("ModalClose");
  if (!isValidElement(children)) return children;

  const child = children as ReactElement<Record<string, unknown>>;
  const childOnClick = child.props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  return cloneElement(child, {
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      childOnClick?.(event);
      if (!event.defaultPrevented) context.setOpen(false);
    },
  });
}

export interface ModalContentProps {
  children: ReactNode;
  /** Accessible name announced by screen readers. */
  ariaLabel: string;
  /** Optional id of descriptive content inside the modal. */
  ariaDescribedBy?: string;
  /** Close on Escape or backdrop press. Default true. */
  dismissible?: boolean;
  /** Render the close control inside the panel's top-right corner. Default true. */
  showCloseButton?: boolean;
  closeButtonLabel?: string;
  /** Panel width. `sm` 480px (confirmations, short forms), `md` 560px, `lg` 720px. */
  size?: ModalSize;
  /** `alertdialog` for destructive confirmations and blocking errors. */
  role?: "dialog" | "alertdialog";
  /** Fires once the fold-out exit has finished; unmount the dialog here. */
  onExitComplete?: () => void;
  className?: string;
  backdropClassName?: string;
}

export type ModalSize = "sm" | "md" | "lg";

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: "max-w-[480px]",
  md: "max-w-[560px]",
  lg: "max-w-[720px]",
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Fade + slight scale. The exit is a short fade: a dismissed dialog should get
// out of the way rather than perform.
const PANEL_HIDDEN = { opacity: 0, scale: 0.98 } as const;
const PANEL_VISIBLE = { opacity: 1, scale: 1 } as const;
const PANEL_EXIT_TRANSITION = { duration: 0.18, ease: EASE_OUT } as const;

const CORNER_MARKS = [
  "-left-[3px] -top-[3px]",
  "-right-[3px] -top-[3px]",
  "-bottom-[3px] -left-[3px]",
  "-bottom-[3px] -right-[3px]",
];

function getFocusableElements(root: HTMLElement | null) {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.tabIndex >= 0,
  );
}

export function ModalContent({
  children,
  ariaLabel,
  ariaDescribedBy,
  dismissible = true,
  showCloseButton = true,
  closeButtonLabel = "Close modal",
  size = "sm",
  role = "dialog",
  onExitComplete,
  className,
  backdropClassName,
}: ModalContentProps) {
  const context = useModalContext("ModalContent");
  const reduce = useReducedMotion() ?? false;
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    // Wait for the portal: a dialog that mounts already open has no panel to
    // focus until the `mounted` re-render has committed.
    if (!context.open || !mounted) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusFrame = requestAnimationFrame(() => {
      // A dialog that focused its own field on mount keeps it.
      if (panelRef.current?.contains(document.activeElement)) return;
      const [firstFocusable] = getFocusableElements(panelRef.current);
      (firstFocusable ?? panelRef.current)?.focus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      // A nested layer (menu, popover, select list) that handled Escape marks it prevented.
      if (event.defaultPrevented) return;
      if (event.key === "Escape" && dismissible) {
        event.preventDefault();
        context.setOpen(false);
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = getFocusableElements(panelRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      // Without a <ModalTrigger>, hand focus back to whatever opened it.
      const trigger = document.getElementById(context.triggerId);
      if (trigger) trigger.focus();
      else if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [context, dismissible, mounted]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence onExitComplete={onExitComplete}>
      {context.open ? (
        <PresenceGate>
          {({ isPresent, gate }) => (
            <>
              <motion.button
                type="button"
                aria-label="Dismiss modal"
                tabIndex={-1}
                disabled={!dismissible}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                {...gate}
                transition={{
                  duration: reduce ? 0.1 : 0.28,
                  ease: EASE_OUT,
                }}
                onClick={() => context.setOpen(false)}
                className={cn(
                  "pointer-events-auto fixed inset-0 z-[100] h-full w-full cursor-default bg-hb-page/80 backdrop-blur-sm",
                  backdropClassName,
                )}
              />

              {/* `inset-4` rather than `inset-0 p-4`: same content box, but the
                  layer stays off the viewport edges. It never takes pointer
                  events, so it carries `inert` alone. See
                  tests/fixed-overlay-edge-sampling.test.tsx. */}
              <div
                inert={!isPresent}
                className="pointer-events-none fixed inset-4 z-[100] flex items-center justify-center overflow-y-auto"
              >
                <div className="flex w-full flex-col items-center py-8">
                  {/* One moving layer: the panel and its register marks fade and
                      scale together. */}
                  <motion.div
                    initial={reduce ? { opacity: 0 } : PANEL_HIDDEN}
                    animate={PANEL_VISIBLE}
                    exit={{
                      ...(reduce ? { opacity: 0 } : PANEL_HIDDEN),
                      transition: PANEL_EXIT_TRANSITION,
                    }}
                    transition={reduce ? { duration: 0.14, ease: EASE_OUT } : SPRING_PANEL}
                    className={cn("relative w-full origin-center", SIZE_CLASS[size])}
                  >
                    <div
                      ref={panelRef}
                      id={context.contentId}
                      role={role}
                      aria-modal="true"
                      aria-label={ariaLabel}
                      aria-describedby={ariaDescribedBy}
                      tabIndex={-1}
                      {...gate}
                      className={cn(
                        "hb-shell pointer-events-auto relative w-full overflow-hidden border border-dashed border-hb-card bg-hb-surface font-hb text-hb-text shadow-2xl shadow-black/60 outline-none",
                        className,
                      )}
                    >
                      {children}

                      {showCloseButton ? (
                        <button
                          type="button"
                          aria-label={closeButtonLabel}
                          onClick={() => context.setOpen(false)}
                          className="absolute right-5 top-3.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-hb-raised/60 text-hb-muted transition-colors hover:bg-hb-raised hover:text-hb-heading"
                        >
                          <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>

                    {/* Register marks sit on the panel's outer corners, outside its
                        `overflow-hidden`. */}
                    <span aria-hidden="true" className="pointer-events-none absolute inset-0">
                      {CORNER_MARKS.map((position) => (
                        <span
                          key={position}
                          className={cn(
                            "absolute h-[6px] w-[6px] border border-hb-border-hover bg-hb-page",
                            position,
                          )}
                        />
                      ))}
                    </span>
                  </motion.div>
                </div>
              </div>
            </>
          )}
        </PresenceGate>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

export interface ModalHeaderProps {
  title: ReactNode;
  /** Small tracked label above the title. */
  eyebrow?: ReactNode;
  /** Icon shown in a round badge before the title. */
  icon?: ReactNode;
  /** Badge colour: orange for actions, red for destructive dialogs. */
  tone?: "accent" | "danger";
  className?: string;
}

/** Title bar. Leaves room for the panel's inset close control. */
export function ModalHeader({
  title,
  eyebrow,
  icon,
  tone = "accent",
  className,
}: ModalHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 border-b border-dashed border-hb-card py-4 pl-6 pr-16",
        className,
      )}
    >
      {icon ? (
        <div
          aria-hidden="true"
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border [&_svg]:h-3.5 [&_svg]:w-3.5",
            tone === "danger"
              ? "border-hb-danger/30 bg-hb-danger/10 text-hb-danger"
              : "border-hb-accent/30 bg-hb-accent/10 text-hb-accent",
          )}
        >
          {icon}
        </div>
      ) : null}
      <div className="min-w-0">
        {eyebrow ? (
          <p className="font-mono-hb text-[10px] uppercase tracking-[0.18em] text-hb-muted">
            {eyebrow}
          </p>
        ) : null}
        <h2 className="font-hb text-lg font-extrabold tracking-tight text-hb-heading">{title}</h2>
      </div>
    </div>
  );
}

/** Padded content region. */
export function ModalBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("px-6 py-6", className)}>{children}</div>;
}

/** Right-aligned action row. */
export function ModalFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-3 border-t border-dashed border-hb-card px-6 py-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * `useOverlayLifecycle`, shaped for this component:
 *
 *   const { modal, content } = useModalLifecycle(onClose);
 *   <Modal {...modal}><ModalContent {...content} ...>
 */
export function useModalLifecycle(onClose: () => void) {
  const { open, onOpenChange, onExitComplete, close } = useOverlayLifecycle(onClose);
  return {
    modal: { open, onOpenChange },
    content: { onExitComplete },
    /** Start the animated close from code (e.g. after a successful submit). */
    close,
  };
}
